import { useQuery } from '@tanstack/react-query';
import { BellRing, CalendarCheck2, CalendarClock, Camera, CircleDollarSign, Clock3, CreditCard, Dog, DollarSign, Hotel, LogIn, LogOut, Scissors, Sparkles, TrendingUp, Truck, UserRound } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { DashboardSummary } from '../../../../../packages/contracts/src/index.js';
import { Button } from '../../components/ui/button';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { ErrorState, LoadingState } from '../../components/ui/state';
import { DateRangeInputs } from '../../components/ui/time-range';
import { api } from '../../shared/api';
import { formatCurrency, formatDate, formatDateTime } from '../../shared/format';
import { copy, labelForServiceCategory } from '../../shared/i18n';
import { businessDateInput, dateRangeForPreset, useUrlListFilters } from '../../shared/listFilters';
import { useAuth } from '../auth/AuthProvider';

const fallback = {
  dogsCurrentlyStaying: 0,
  todaysCheckIns: 0,
  todaysCheckOuts: 0,
  occupancy: 0,
  pendingGrooming: 0,
  revenueTodayCents: 0,
  revenueMonthCents: 0,
  paymentCountToday: 0,
  periodLabel: 'Semana',
  periodRevenueCents: 0,
  periodPaymentCount: 0,
  revenueByType: [],
  revenueByServiceCategory: [],
  chart: [],
  recentActivity: [],
  alerts: [],
};

export function DashboardPage() {
  const { user } = useAuth();
  const isAdministrator = user?.groups.includes('Administrator') ?? false;
  const today = useMemo(() => businessDateInput(), []);
  const defaults = useMemo(() => ({ period: 'today', from: today, to: today }), [today]);
  const [searchParams] = useSearchParams();
  const { values: filters, setValues } = useUrlListFilters(defaults, { keepDefaults: true });
  useEffect(() => {
    if (!searchParams.has('period') || !searchParams.has('from') || !searchParams.has('to')) setValues(defaults);
  }, [defaults, searchParams, setValues]);
  const summaryQuery = useQuery({
    queryKey: ['dashboard-summary', filters],
    queryFn: () => api.dashboard({ period: filters.period, from: filters.from, to: filters.to }),
  });
  const paymentsQuery = useQuery({ queryKey: ['payments', 'dashboard', filters], queryFn: () => api.payments.list({ limit: 100, from: filters.from, to: filters.to }), enabled: isAdministrator });
  const staysQuery = useQuery({ queryKey: ['stays', 'dashboard', filters], queryFn: () => api.stays.list({ limit: 100, from: filters.from, to: filters.to }) });
  const daycaresQuery = useQuery({ queryKey: ['daycares', 'dashboard', filters], queryFn: () => api.daycares.list({ limit: 100, from: filters.from, to: filters.to }) });
  const appointmentsQuery = useQuery({ queryKey: ['appointments', 'dashboard', filters], queryFn: () => api.appointments.list({ limit: 100, from: filters.from, to: filters.to }) });
  const dogsQuery = useQuery({ queryKey: ['dogs', 'dashboard'], queryFn: () => api.dogs.list({ limit: 100 }) });
  const customersQuery = useQuery({ queryKey: ['customers', 'dashboard'], queryFn: () => api.customers.list({ limit: 100 }) });

  if (summaryQuery.isLoading) return <LoadingState label={copy.dashboard.loading} />;
  if (summaryQuery.isError) return <ErrorState message={summaryQuery.error.message} />;

  const summary = summaryQuery.data ?? fallback;
  const payments = paymentsQuery.data?.items ?? [];
  const stays = staysQuery.data?.items ?? [];
  const daycares = daycaresQuery.data?.items ?? [];
  const appointments = appointmentsQuery.data?.items ?? [];
  const dogs = dogsQuery.data?.items ?? [];
  const customers = customersQuery.data?.items ?? [];
  const financialStats = financialStatsFor(payments);
  const serviceIncome = summary.revenueByServiceCategory ?? [];
  const serviceIncomeTotalCents = serviceIncome.reduce((total, item) => total + item.amountCents, 0);
  const serviceIncomeMaxCents = Math.max(...serviceIncome.map((item) => item.amountCents), 1);
  const membershipRevenueCents = summary.revenueByType.find((item) => item.type === 'membership')?.amountCents ?? 0;
  const membershipBarMaxCents = Math.max(serviceIncomeMaxCents, membershipRevenueCents, 1);
  const periodChart = [
    ...(summary.chart ?? []),
    ...(isAdministrator ? [{ label: 'Membresias', value: membershipRevenueCents / 100, amountCents: membershipRevenueCents }] : []),
  ];
  const operationsStats = [
    { label: 'Hoteles del periodo', value: stays.length },
    { label: 'Guarderias del periodo', value: daycares.length },
    { label: 'Citas del periodo', value: appointments.length },
    { label: 'Perros activos', value: dogs.filter((dog) => dog.status === 'active').length },
    { label: 'Clientes registrados', value: customers.length },
    { label: 'Guarderias por entradas', value: daycares.filter((daycare) => daycare.billingMode === 'entrance-package').length },
  ];
  const appointmentStatusStats = [
    { label: 'Citas programadas', value: appointments.filter((appointment) => appointment.status === 'scheduled').length },
    { label: 'Citas terminadas', value: appointments.filter((appointment) => appointment.status === 'done').length },
  ];
  const operationalMetrics = [
    { label: copy.dashboard.metrics.currentlyStaying, value: summary.dogsCurrentlyStaying, icon: Dog, tone: 'text-[#75816a]', bg: 'bg-[#eef3ea]' },
    { label: copy.dashboard.metrics.todaysCheckIns, value: summary.todaysCheckIns, icon: CalendarCheck2, tone: 'text-[#cf7766]', bg: 'bg-[#f3e5df]' },
    { label: copy.dashboard.metrics.todaysCheckOuts, value: summary.todaysCheckOuts, icon: CalendarClock, tone: 'text-[#7d4f43]', bg: 'bg-[#f8eee9]' },
    { label: copy.dashboard.metrics.occupancy, value: summary.occupancy, icon: Hotel, tone: 'text-[#4f5d49]', bg: 'bg-[#eef3ea]' },
    { label: copy.dashboard.metrics.pendingGrooming, value: summary.pendingGrooming, icon: Scissors, tone: 'text-[#a96654]', bg: 'bg-[#f3e5df]' },
  ];
  const moneyMetrics = [
    { label: copy.dashboard.metrics.revenueToday, value: formatCurrency(summary.revenueTodayCents), icon: DollarSign, tone: 'text-[#596a52]', bg: 'bg-[#eef3ea]' },
    { label: 'Pagos de hoy', value: summary.paymentCountToday, icon: CreditCard, tone: 'text-[#7d4f43]', bg: 'bg-[#f8eee9]' },
    { label: copy.dashboard.metrics.revenueMonth, value: formatCurrency(summary.revenueMonthCents), icon: TrendingUp, tone: 'text-[#8b695f]', bg: 'bg-[#f8eee9]' },
  ];
  const metrics = isAdministrator ? [...operationalMetrics, ...moneyMetrics] : operationalMetrics;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-slate-950">{copy.dashboard.title}</h1>
        <p className="mt-1 text-sm text-slate-500">{copy.dashboard.body}</p>
      </div>
      <SegmentedControl
        value={filters.period as 'today' | 'week' | 'month' | 'year'}
        options={[{ id: 'today', label: 'Hoy' }, { id: 'week', label: 'Semana' }, { id: 'month', label: 'Mes' }, { id: 'year', label: 'Año' }]}
        onChange={(period) => setValues(period === 'today' ? { period, from: today, to: today } : period === 'year' ? { period, from: `${today.slice(0, 4)}-01-01`, to: today } : { period, ...dateRangeForPreset(period) })}
        ariaLabel="Periodo del tablero"
        className="sm:max-w-xl"
      />
      <DateRangeInputs
        start={{ id: 'dashboardFrom', value: filters.from, onChange: (event) => setValues({ period: 'custom', from: event.target.value }) }}
        end={{ id: 'dashboardTo', value: filters.to, onChange: (event) => setValues({ period: 'custom', to: event.target.value }) }}
        startLabel="Fecha inicial"
        endLabel="Fecha final"
      />

      {isAdministrator ? (
        <section className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div><h2 className="text-base font-semibold text-[#223126]">Ingresos por tipo</h2><p className="text-sm text-[#687461]">{summary.periodLabel}</p></div>
            <Link className="inline-flex h-9 items-center gap-2 rounded-md border border-[#d6ded0] bg-white px-3 text-sm font-semibold text-[#334033] hover:bg-[#eef3ea]" to={`/admin/payments/history?range=custom&from=${encodeURIComponent(filters.from)}&to=${encodeURIComponent(filters.to)}`}><CreditCard className="h-4 w-4" aria-hidden="true" />Ver pagos</Link>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
            {summary.revenueByType.map((group) => <IncomeTypeCard key={group.type} type={group.type} amountCents={group.amountCents} />)}
            <div className="rounded-md border border-[#75816a] bg-[#eef3ea] p-4"><p className="text-xs font-semibold uppercase text-[#506047]">Total</p><p className="mt-2 text-2xl font-semibold text-[#223126]">{formatCurrency(summary.periodRevenueCents)}</p></div>
          </div>
          <div className="grid gap-3 lg:grid-cols-[0.85fr_1.15fr]">
            <div className="rounded-md border border-[#cf7766] bg-[#fff8f4] p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-white text-[#a65443] ring-1 ring-[#efd2c8]">
                  <Scissors className="h-5 w-5" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase text-[#8a5a4f]">Servicios por categoria</p>
                  <p className="mt-2 text-3xl font-semibold text-[#223126]">{formatCurrency(serviceIncomeTotalCents)}</p>
                  <p className="mt-1 text-sm text-[#687461]">Detalle del ingreso de servicios en el periodo.</p>
                </div>
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {serviceIncome.map((group) => <ServiceIncomeRow key={group.category} category={group.category} amountCents={group.amountCents} maxCents={serviceIncomeMaxCents} />)}
              <MembershipIncomeRow amountCents={membershipRevenueCents} maxCents={membershipBarMaxCents} />
            </div>
          </div>
        </section>
      ) : null}

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {metrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <Card key={metric.label}>
              <CardContent className="flex items-center gap-3">
                <div className={`flex h-10 w-10 items-center justify-center rounded-md ${metric.bg} ${metric.tone}`}>
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium uppercase tracking-normal text-slate-500">{metric.label}</p>
                  <p className="mt-1 truncate text-2xl font-semibold text-slate-950">{metric.value}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </section>

      <section className="grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold">Métricas del periodo</h2>
          </CardHeader>
          <CardContent className="space-y-3">
            {periodChart.map((item) => {
              const max = Math.max(...periodChart.map((entry) => entry.value), 1);
              return (
                <div key={item.label} className="grid gap-1">
                  <div className="flex justify-between gap-3 text-sm">
                    <span className="font-medium text-[#334033]">{item.label}</span>
                    <span className="text-[#5f6b58]">{typeof item.amountCents === 'number' ? formatCurrency(item.amountCents) : item.value}</span>
                  </div>
                  <div className="h-3 overflow-hidden rounded-sm bg-[#eef3ea]">
                    <div className="h-full bg-[#cf7766]" style={{ width: `${Math.max(4, (item.value / max) * 100)}%` }} />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock3 className="h-4 w-4 text-[#75816a]" aria-hidden="true" />
              <h2 className="text-sm font-semibold">{copy.dashboard.recentActivity}</h2>
              <Link className="ml-auto text-xs font-semibold text-[#2f6b38] hover:underline" to={`/admin/dashboard/activity?range=custom&from=${encodeURIComponent(filters.from)}&to=${encodeURIComponent(filters.to)}`}>Ver todo</Link>
            </div>
          </CardHeader>
          <CardContent>
            {summary.recentActivity.length === 0 ? (
              <p className="text-sm text-slate-500">{copy.dashboard.noRecentActivity}</p>
            ) : (
              <div className="divide-y divide-[#e5eadf] rounded-md border border-[#d6ded0]">
                {summary.recentActivity.slice(0, 10).map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3 p-3">
                    <ActivityIcon type={activity.type} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[#223126]">{activity.message}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#687461]">
                        {activity.actorName ? <span className="inline-flex items-center gap-1"><UserRound className="h-3.5 w-3.5" aria-hidden="true" />{activity.actorName}</span> : null}
                        <span>{formatDateTime(activity.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <BellRing className="h-4 w-4 text-[#cf7766]" aria-hidden="true" />
              <h2 className="text-sm font-semibold">{copy.dashboard.alerts}</h2>
              <Link className="ml-auto text-xs font-semibold text-[#2f6b38] hover:underline" to={`/admin/dashboard/alerts?range=custom&from=${encodeURIComponent(filters.from)}&to=${encodeURIComponent(filters.to)}`}>Ver todo</Link>
            </div>
          </CardHeader>
          <CardContent>
            {summary.alerts.length === 0 ? (
              <p className="text-sm text-slate-500">{copy.dashboard.noActiveAlerts}</p>
            ) : (
              <div className="divide-y divide-[#e5eadf] rounded-md border border-[#d6ded0]">
                {summary.alerts.slice(0, 10).map((alert) => (
                  <div key={alert.id} className="flex items-start gap-3 p-3">
                    <AlertIcon type={alert.type} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[#223126]">{alert.message}</p>
                      <p className="mt-1 text-xs text-[#687461]">{formatAlertSchedule(alert)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold">Operacion</h2>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            {operationsStats.map((item) => (
              <div key={item.label} className="rounded-md border border-[#d6ded0] bg-[#fbfcfa] p-3">
                <p className="text-xs font-medium uppercase text-[#687461]">{item.label}</p>
                <p className="mt-1 text-2xl font-semibold text-[#223126]">{item.value}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        {isAdministrator ? <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold">Mezcla financiera</h2>
          </CardHeader>
          <CardContent className="space-y-3">
            {financialStats.length === 0 ? <p className="text-sm text-slate-500">Sin pagos en este periodo.</p> : null}
            {financialStats.map((item) => {
              const max = Math.max(...financialStats.map((entry) => entry.amountCents), 1);
              return (
                <div key={item.label} className="grid gap-1">
                  <div className="flex justify-between gap-3 text-sm">
                    <span className="font-medium capitalize text-[#334033]">{item.label}</span>
                    <span className="text-[#5f6b58]">{formatCurrency(item.amountCents)}</span>
                  </div>
                  <div className="h-3 overflow-hidden rounded-sm bg-[#eef3ea]">
                    <div className="h-full bg-[#75816a]" style={{ width: `${Math.max(4, (item.amountCents / max) * 100)}%` }} />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card> : null}

        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold">Citas</h2>
          </CardHeader>
          <CardContent className="space-y-3">
            {appointmentStatusStats.map((item) => (
              <div key={item.label} className="flex items-center justify-between rounded-md border border-[#d6ded0] bg-[#fbfcfa] px-3 py-2 text-sm">
                <span className="font-medium text-[#334033]">{item.label}</span>
                <span className="text-lg font-semibold text-[#223126]">{item.value}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

function ActivityIcon({ type }: { readonly type: DashboardSummary['recentActivity'][number]['type'] }) {
  const icons = { payment: CircleDollarSign, grooming: Scissors, 'check-in': LogIn, 'check-out': LogOut, photo: Camera, other: Clock3 };
  const Icon = icons[type];
  return <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#eef3ea] text-[#596a52]"><Icon className="h-4 w-4" aria-hidden="true" /></span>;
}

function AlertIcon({ type }: { readonly type: DashboardSummary['alerts'][number]['type'] }) {
  const icons = { grooming: Scissors, spa: Sparkles, transportation: Truck, 'stay-check-in': Hotel, 'stay-check-out': LogOut, 'daycare-check-in': LogIn, 'daycare-check-out': LogOut, other: CalendarClock };
  const Icon = icons[type];
  return <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#f8eee9] text-[#a96654]"><Icon className="h-4 w-4" aria-hidden="true" /></span>;
}

function formatAlertSchedule(alert: DashboardSummary['alerts'][number]) {
  return alert.type === 'stay-check-in' || alert.type === 'stay-check-out' ? formatDate(alert.scheduledAt) : formatDateTime(alert.scheduledAt);
}

const incomeTypeLabels = { stay: 'Hotel', daycare: 'Guardería', service: 'Servicios', membership: 'Membresías', other: 'Otros' } as const;

function IncomeTypeCard({ type, amountCents }: { type: keyof typeof incomeTypeLabels; amountCents: number }) {
  return <div className="rounded-md border border-[#d6ded0] bg-white p-4 shadow-sm"><p className="text-xs font-semibold uppercase text-[#687461]">{incomeTypeLabels[type]}</p><p className="mt-2 text-xl font-semibold text-[#223126]">{formatCurrency(amountCents)}</p></div>;
}

const serviceIncomeStyles = {
  daycare: { icon: Dog, bg: 'bg-[#eef3ea]', fill: 'bg-[#75816a]', text: 'text-[#4f5d49]' },
  boarding: { icon: Hotel, bg: 'bg-[#f8eee9]', fill: 'bg-[#b57968]', text: 'text-[#7d4f43]' },
  grooming: { icon: Scissors, bg: 'bg-[#fff4ef]', fill: 'bg-[#cf7766]', text: 'text-[#9a4f40]' },
  spa: { icon: Sparkles, bg: 'bg-[#f4f1ff]', fill: 'bg-[#8271b5]', text: 'text-[#5f528e]' },
  transportation: { icon: Truck, bg: 'bg-[#eef7fb]', fill: 'bg-[#4f8ea4]', text: 'text-[#386d7f]' },
  medical: { icon: CalendarCheck2, bg: 'bg-[#f1f7ef]', fill: 'bg-[#6f996b]', text: 'text-[#52734f]' },
  treats: { icon: Dog, bg: 'bg-[#fff8e8]', fill: 'bg-[#c59442]', text: 'text-[#87662e]' },
  fee: { icon: CreditCard, bg: 'bg-[#fff8e8]', fill: 'bg-[#c59442]', text: 'text-[#87662e]' },
  other: { icon: CircleDollarSign, bg: 'bg-[#f3f4f6]', fill: 'bg-[#7a8794]', text: 'text-[#56616d]' },
} as const;

function ServiceIncomeRow({ category, amountCents, maxCents }: { readonly category: DashboardSummary['revenueByServiceCategory'][number]['category']; readonly amountCents: number; readonly maxCents: number }) {
  const style = serviceIncomeStyles[category];
  const Icon = style.icon;
  return (
    <div className="rounded-md border border-[#d6ded0] bg-white p-3 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${style.bg} ${style.text}`}>
          <Icon className="h-4 w-4" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <p className="truncate text-sm font-semibold text-[#223126]">{labelForServiceCategory(category)}</p>
            <p className="shrink-0 text-sm font-semibold text-[#223126]">{formatCurrency(amountCents)}</p>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-sm bg-[#eef1e9]">
            <div className={`h-full ${style.fill}`} style={{ width: `${amountCents === 0 ? 0 : Math.max(6, (amountCents / maxCents) * 100)}%` }} />
          </div>
        </div>
      </div>
    </div>
  );
}

function MembershipIncomeRow({ amountCents, maxCents }: { readonly amountCents: number; readonly maxCents: number }) {
  return (
    <div className="rounded-md border border-[#d6ded0] bg-white p-3 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#fff8e8] text-[#87662e]">
          <CreditCard className="h-4 w-4" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <p className="truncate text-sm font-semibold text-[#223126]">Membresias</p>
            <p className="shrink-0 text-sm font-semibold text-[#223126]">{formatCurrency(amountCents)}</p>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-sm bg-[#eef1e9]">
            <div className="h-full bg-[#c59442]" style={{ width: `${amountCents === 0 ? 4 : Math.max(6, (amountCents / maxCents) * 100)}%` }} />
          </div>
        </div>
      </div>
    </div>
  );
}

function financialStatsFor(payments: Array<{ readonly method: string; readonly totalCents: number }>) {
  const byMethod = new Map<string, number>();
  for (const payment of payments) byMethod.set(payment.method, (byMethod.get(payment.method) ?? 0) + payment.totalCents);
  return [...byMethod.entries()].map(([label, amountCents]) => ({ label, amountCents, value: amountCents / 100 }));
}
